/**
 * PersonaPickerModal
 * Modal for selecting a persona/role when creating workspace or inviting staff
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../v2/ui/dialog';
import { Button } from '../../v2/ui/button';
import { useTheme } from '../../../utils/theme';
import { cn } from '../../design-system';
import { Search, Check, Users, Shield, Briefcase, Stethoscope, Settings, UserCircle, ChevronDown, ChevronUp } from 'lucide-react';

// Tier icons mapping
const TIER_ICONS = {
  EXECUTIVE: Briefcase,
  COACHING: Users,
  DEVELOPMENT: UserCircle,
  SCOUTING: Search,
  MEDICAL: Stethoscope,
  OPERATIONS: Settings,
  PLAYER: Shield,
};

// Tier colors
const TIER_COLORS = {
  EXECUTIVE: 'text-yellow-400',
  COACHING: 'text-blue-400',
  DEVELOPMENT: 'text-green-400',
  SCOUTING: 'text-purple-400',
  MEDICAL: 'text-red-400',
  OPERATIONS: 'text-gray-400',
  PLAYER: 'text-orange-400',
};

// Tier display order
const TIER_ORDER = ['EXECUTIVE', 'COACHING', 'DEVELOPMENT', 'SCOUTING', 'MEDICAL', 'OPERATIONS', 'PLAYER'];

/**
 * PersonaPickerModal component
 */
export function PersonaPickerModal({
  isOpen,
  onClose,
  onSelect,
  personas = {},
  selectedPersonaId = null,
  title = 'Select Role',
  description = 'Choose the role that best describes your position.',
}) {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTiers, setExpandedTiers] = useState(new Set(['EXECUTIVE', 'COACHING']));

  // Filter personas by search query
  const filteredPersonas = useMemo(() => {
    if (!searchQuery.trim()) return personas;

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(personas).forEach(([tier, personaList]) => {
      const matches = personaList.filter(
        p =>
          p.name.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.code.toLowerCase().includes(query)
      );
      if (matches.length > 0) {
        filtered[tier] = matches;
      }
    });

    return filtered;
  }, [personas, searchQuery]);

  // Toggle tier expansion
  const toggleTier = (tier) => {
    setExpandedTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      return next;
    });
  };

  // Handle persona selection
  const handleSelect = (persona) => {
    onSelect(persona);
    onClose();
  };

  // Sorted tiers
  const sortedTiers = useMemo(() => {
    return TIER_ORDER.filter(tier => filteredPersonas[tier]?.length > 0);
  }, [filteredPersonas]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'max-w-2xl max-h-[80vh] overflow-hidden flex flex-col',
          'bg-card border-border'
        )}
      >
        <DialogHeader>
          <DialogTitle className={'text-white'}>
            {title}
          </DialogTitle>
          <DialogDescription className={'text-gray-400'}>
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative mb-4">
          <Search className={cn(
            'absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4',
            'text-gray-500'
          )} />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              'w-full pl-10 pr-4 py-2 rounded-lg border text-sm outline-none transition-colors',
              'bg-card/50 border-border/50 text-white placeholder-gray-500 focus:border-primary/50'
            )}
          />
        </div>

        {/* Personas List */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {sortedTiers.map((tier) => {
            const TierIcon = TIER_ICONS[tier] || Users;
            const isExpanded = expandedTiers.has(tier);
            const personaList = filteredPersonas[tier] || [];

            return (
              <div key={tier} className={cn(
                'rounded-lg border overflow-hidden',
                'border-border/50'
              )}>
                {/* Tier Header */}
                <button
                  onClick={() => toggleTier(tier)}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-3 transition-colors',
                    'bg-card/30 hover:bg-card/50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <TierIcon className={cn('w-5 h-5', TIER_COLORS[tier])} />
                    <span className={cn(
                      'font-medium capitalize',
                      'text-white'
                    )}>
                      {tier.toLowerCase().replace('_', ' ')}
                    </span>
                    <span className={cn(
                      'text-xs px-2 py-0.5 rounded-full',
                      'bg-border/50 text-gray-400'
                    )}>
                      {personaList.length}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {/* Persona Items */}
                {isExpanded && (
                  <div className={cn(
                    'divide-y',
                    'divide-border/30'
                  )}>
                    {personaList.map((persona) => {
                      const isSelected = persona.id === selectedPersonaId;
                      return (
                        <button
                          key={persona.id}
                          onClick={() => handleSelect(persona)}
                          className={cn(
                            'w-full flex items-center gap-4 px-4 py-3 text-left transition-colors',
                            isSelected
                              ? 'bg-primary/10 border-l-2 border-primary'
                              : 'hover:bg-card/30'
                          )}
                        >
                          {/* Persona Icon/Emoji */}
                          <span className="text-2xl w-8 text-center">
                            {persona.icon || '👤'}
                          </span>

                          {/* Persona Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                'font-medium',
                                'text-white'
                              )}>
                                {persona.name}
                              </span>
                              {persona.defaultAccessLevel && (
                                <span className={cn(
                                  'text-xs px-1.5 py-0.5 rounded',
                                  persona.defaultAccessLevel === 'ADMIN'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : persona.defaultAccessLevel === 'STANDARD'
                                      ? 'bg-blue-500/20 text-blue-400'
                                      : 'bg-gray-500/20 text-gray-400'
                                )}>
                                  {persona.defaultAccessLevel}
                                </span>
                              )}
                            </div>
                            {persona.description && (
                              <p className={cn(
                                'text-sm truncate',
                                'text-gray-400'
                              )}>
                                {persona.description}
                              </p>
                            )}
                          </div>

                          {/* Selected Indicator */}
                          {isSelected && (
                            <Check className="w-5 h-5 text-primary flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Empty State */}
          {sortedTiers.length === 0 && (
            <div className={cn(
              'text-center py-8',
              'text-gray-500'
            )}>
              No roles found matching "{searchQuery}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t border-border/50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PersonaPickerModal;
